import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL, getMissingDocumentTypes } from '@pelicargo/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { HiOutlineArrowUpTray, HiOutlineCheck } from 'react-icons/hi2';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { documentSchema } from '../../utils/schemas';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleForm } from '../SimpleForm';
import { DocumentRow } from './DocumentRow';

type Props = {
  defaultDocuments?: Partial<Document>[];
  documentTypes?: DocumentType[];
  isApprovable?: boolean;
  isOpen: boolean;
  onClose: () => void;
};

type DocumentFormValues = {
  documents: Partial<Document>[];
};

export const DocumentsModal = ({
  isOpen,
  onClose,
  documentTypes,
  defaultDocuments: documents,
  isApprovable = false,
}: Props) => {
  const { requestId, quoteId } = useParams<{ requestId: string; quoteId: string }>();
  const { mutateAsync: updateDocuments, isLoading } = trpc.updateDocuments.useMutation();
  const { mutateAsync: approveDocuments, isLoading: isApproving } = trpc.approveDocuments.useMutation();
  const methods = useForm<DocumentFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(z.object({ documents: z.array(documentSchema).min(1) })),
    defaultValues: { documents },
  });
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const { targetAccount } = useTargetAccount();

  const cleanedDocuments = useMemo(() => {
    const missingDocumentTypes = getMissingDocumentTypes(documents, documentTypes);

    // Include all current documents in the list. If a document type is not in the list, it will be added.
    const finalDocuments = [
      ...documents,
      ...missingDocumentTypes.map((type) => ({ type }) as Partial<Document>),
    ].filter((doc) => doc?.type || doc?.id);

    return finalDocuments;
  }, [documentTypes, documents]);

  const currentDocuments = methods.watch('documents').filter(Boolean);
  const updatedDocuments = currentDocuments.filter((doc) => {
    const existingDoc = documents.find((d) => d.id === doc?.id);
    return existingDoc && doc.type !== existingDoc.type && !!doc.type;
  });
  const isUpdateButtonsDisabled = updatedDocuments.length === 0;
  const nonAccountDocuments = currentDocuments.filter((doc) => doc.account_id !== targetAccount?.id);
  const isApproveButtonDisabled = nonAccountDocuments
    .filter((doc) => DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type))
    .every((doc) => !!doc.approved_at);

  useEffect(() => {
    if (documents.length) methods.setValue('documents', documents);
  }, [documents, documents.length, methods]);

  const renderDocumentRow = (document: Partial<Document>) => <DocumentRow key={document.id} document={document} />;

  const handleUpdate = async () => {
    await updateDocuments({
      documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
      quote_id: Number(quoteId),
    });
    onClose();
  };

  const handleUpdateAndNotify = async () => {
    try {
      setIsNotifyLoading(true);
      await updateDocuments({
        documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
        quote_id: Number(quoteId),
        shouldNotify: true,
      });

      onClose();
    } finally {
      setIsNotifyLoading(false);
    }
  };
  const handleApproveAndNotify = async () => {
    const data = methods.getValues();
    const nonApprovedDocuments = data.documents.filter(
      (doc) =>
        doc.id &&
        !doc.approved_at &&
        doc.account_id !== targetAccount?.id &&
        DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type),
    );
    await approveDocuments({
      documentIds: nonApprovedDocuments.map((doc) => doc.id),
      quote_id: Number(quoteId),
      shouldNotify: true,
    });

    onClose();
  };

  return (
    <Modal size={{ base: 'full', lg: '4xl' }} isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxH={{ base: 'full', lg: '83vh' }} overflow="auto">
        <SimpleForm onSubmit={handleUpdate} {...(methods as UseFormReturn<any, any>)}>
          <ModalCloseButton />
          <ModalHeader>
            <Stack direction="row" align="center" justify="space-between">
              <Stack spacing="1">
                <Heading variant="h4">Manage Documents</Heading>
              </Stack>
            </Stack>
          </ModalHeader>

          <ModalBody h="full">
            <Stack divider={<Divider />} spacing="4">
              <Stack>{cleanedDocuments.map(renderDocumentRow)}</Stack>
              <SimpleDropzone name="documents" requestId={Number(requestId)} isTypeSelectable />
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Stack width="full" justify="flex-end" direction="row" spacing="3">
              <Button
                variant="outline"
                colorScheme="primary"
                title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                onClick={handleUpdate}
                isLoading={isLoading && !isNotifyLoading}
                isDisabled={isUpdateButtonsDisabled}
                leftIcon={<HiOutlineArrowUpTray />}
              >
                Update
              </Button>
              <Button
                variant="solid"
                colorScheme="primary"
                title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                onClick={handleUpdateAndNotify}
                isLoading={isNotifyLoading}
                isDisabled={isUpdateButtonsDisabled}
                leftIcon={<HiOutlineArrowUpTray />}
              >
                Update & Notify
              </Button>
              <Show if={isApprovable}>
                <Button
                  variant="solid"
                  colorScheme="green"
                  title={isApproveButtonDisabled ? 'No documents to approve' : undefined}
                  onClick={handleApproveAndNotify}
                  isLoading={isApproving}
                  isDisabled={isApproveButtonDisabled}
                  leftIcon={<HiOutlineCheck />}
                >
                  Approve & Notify
                </Button>
              </Show>
            </Stack>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
